import { useQuery, useMutation, useQueryClient } from 'react-query'; // Hooks do react-query para requisições e cache
import { FerramentaVideoService } from 'core/http/service/ferramentaVideo/FerramentaVideo.service';
import { IFVContainer } from 'pages/ferramentaVideo/interfaces/FVContainer.interface';

const service = new FerramentaVideoService();

// Função para buscar todas as ferramentas de vídeo
const fetchVideoTools = async (): Promise<IFVContainer[]> => {
    const { data } = await service.fetchContainer();
    return data;
};

const fetchUniqueFerramentaVideo = async (id: number): Promise<IFVContainer | null> => {
    const { data } = await service.fetchContainerById(id);
    return data;
};

// Função para criar uma nova ferramenta de vídeo
const createVideoTool = async (tool: Omit<IFVContainer, 'id'>): Promise<IFVContainer> => {
    // Adiciona um id vazio para satisfazer o tipo ContainerTool exigido pelo service
    return service.insertContainer({ ...tool, id: -1 });
};

// Função para atualizar uma ferramenta de vídeo existente
const updateVideoTool = async (tool: IFVContainer): Promise<IFVContainer> => {
    return service.updateFerramentaVideo(tool);
};

// Função para deletar uma ferramenta de vídeo pelo id
const deleteVideoTool = async (id: number): Promise<void> => {
    const { status } = await service.deleteFerramentaVideo(id);
    return status;
};

// Hook principal que expõe as operações de CRUD usando react-query
export function useFetchFVContainer() {
    const queryClient = useQueryClient(); // Permite invalidar e atualizar queries no cache

    // Hook para listar ferramentas de vídeo (GET)
    const listQuery = useQuery<IFVContainer[]>('containerTools', fetchVideoTools);

    // Corrigido: Hook para buscar ferramenta de vídeo única por id (GET)
    const useUniqueFerramentaVideo = (id: number) =>
        useQuery<IFVContainer | null>(['containerToolUnique', id], ({ queryKey }) => fetchUniqueFerramentaVideo(queryKey[1] as number));

    // Hook para criar ferramenta de vídeo (POST)
    const createContainerMutation = useMutation(createVideoTool, {
        onSuccess: () => {
            queryClient.invalidateQueries('containerTools'); // Atualiza a lista após criar
        },
    });

    // Hook para atualizar ferramenta de vídeo (PUT)
    const updateContainerMutation = useMutation(updateVideoTool, {
        onSuccess: () => {
            queryClient.invalidateQueries('containerTools'); // Atualiza a lista após atualizar
        },
    });

    // Hook para deletar ferramenta de vídeo (DELETE)
    const deleteContainerMutation = useMutation(deleteVideoTool, {
        onSuccess: () => {
            queryClient.invalidateQueries('containerTools'); // Atualiza a lista após deletar
        },
    });

    const fetchContainerById = async (id: number): Promise<IFVContainer | undefined> => {
        const data = listQuery?.data?.filter(container => container.id === id)[0];
        return data;
    };

    // Retorna todos os hooks para uso no componente
    return {
        listQuery,
        createContainerMutation,
        updateContainerMutation,
        deleteContainerMutation,
        fetchContainerById,
        useUniqueFerramentaVideo, // exporta o novo hook
    };
}
